import { useEffect, useMemo, useReducer, useState } from 'react';
import CreateLiquidityPosition, {
  CreateLiquidityPositionProps,
} from './CreateLiquidityPosition';
import { CreateLiquidityPositionDetails, Step } from './types';
import useAddress from '@/application/read/web3/useAddress';
import useUserAssets from '@/application/read/assets/useUserAssets';
import {
  getMaxPercentages,
  getMinPercentages,
} from '../../forms/CreatePositionForms/SetRangeForm/utils';
import useLiquidityPools from '@/application/read/pools/useLiquidityPools';
import {
  Address,
  FeeTier,
  TICK_PRICE_BASE,
  VAULT_FEE_TIER,
  Zero,
} from 'nftx.js';
import { SelectPoolFormFields } from '../../forms/CreatePositionForms/SelectPoolForm';
import useVault from '@/application/read/vaults/useVault';
import { useBalance } from 'wagmi';
import { parseEther } from 'viem';
import useBalances from '@/application/read/tokens/useBalances';
import Settings from '../../widgets/Settings';
import getWeakestStatus from '@/presentation/utils/getWeakestStatus';
import {
  calculatePriceFromTick,
  isAddressToken0,
} from '@/domain/ticks/helpers';
import usePoolCurrentTick from '@/application/read/pools/usePoolCurrentTick';
import { useRouter } from 'next/navigation';
import { useNetwork } from '@/presentation/context/network';

export interface CreateLiquidityPositionContainerProps
  extends Pick<
    CreateLiquidityPositionProps,
    'vaultId' | 'icon' | 'isNewVault' | 'withinModal'
  > {
  poolType?: SelectPoolFormFields['poolType'];
  using?: SelectPoolFormFields['using'];
  feeTier?: FeeTier;
  previousUrl?: string;
}

const reducer = (
  state: CreateLiquidityPositionDetails,
  action: Partial<CreateLiquidityPositionDetails>
) => ({
  ...state,
  ...action,
});

const CreateLiquidityPositionContainer = ({
  vaultId,
  icon,
  isNewVault = false,
  poolType = 'pair',
  withinModal,
  feeTier = VAULT_FEE_TIER,
  using = 'nft',
  previousUrl = '',
}: CreateLiquidityPositionContainerProps) => {
  const [step, setStep] = useState<Step>('pool');
  const { replace } = useRouter();

  const { network, slug } = useNetwork();
  const { data: vault, status: vaultStatus } = useVault({ vaultId });
  const collection = vault?.asset?.id;
  const { data: pools } = useLiquidityPools(
    { vaultId },
    { enabled: !!vaultId }
  );

  const [state, dispatch] = useReducer(reducer, {
    poolType,
    feeTier: feeTier.toString(),
    items: [],
    using,
    ethAmount: '0',
    maxPriceInEth: '0',
    minPriceInEth: '0',
    tickLower: 0,
    tickUpper: 0,
    tokenAmount: '0',
    fullRange: false,
    maxPercent: getMaxPercentages(poolType).pop(),
    minPercent: getMinPercentages(poolType)[0],
    tick: '10200',
    price: `${TICK_PRICE_BASE ** parseFloat('10200')}`,
    liquidityAmount: '0',
  } as CreateLiquidityPositionDetails);

  useEffect(() => {
    if (!withinModal && state.poolType !== poolType) {
      replace(
        `/${slug}/create/position/liquidity?vaultId=${vaultId}&poolType=${state.poolType}`
      );
    }
  }, [state.poolType, poolType, withinModal, vaultId, replace, slug]);

  const pool = useMemo(
    () => pools?.find((pool) => pool.feeTier === Number(state.feeTier)),
    [pools, state.feeTier]
  );

  const isVToken0 = isAddressToken0(vault?.token?.id, network);

  const userAddress = useAddress();
  const { data: assets, status: assetsStatus } = useUserAssets({
    userAddress,
    assetAddress: collection,
    vaultId,
    mintable: isNewVault ? undefined : true,
  });

  const { data: ethBalance } = useBalance({ address: userAddress });
  const { data: balances, status: balancesStatus } = useBalances({
    userAddress,
  });

  const { data: currentTick = 0n } = usePoolCurrentTick(pool?.id);
  const currentNFTPriceInETH = calculatePriceFromTick(currentTick, isVToken0);

  const amountMax = useMemo(() => {
    if (
      (state.poolType === 'pair' || state.poolType === 'nft') &&
      state.using === 'vtoken'
    ) {
      return balances?.find((b) => b.vaultId === vaultId)?.balance ?? Zero;
    }
    const totalItems = state.items.reduce((total, item) => {
      const q = Number(item.quantity ?? 1n);
      return total + q;
    }, 0);
    return parseEther(`${totalItems}`);
  }, [balances, state.items, state.poolType, state.using, vaultId]);

  if (!vault || !collection) {
    return null;
  }

  const status = getWeakestStatus(vaultStatus, assetsStatus, balancesStatus);

  return (
    <CreateLiquidityPosition
      isNewVault={isNewVault}
      status={status}
      step={step}
      setStep={setStep}
      collection={collection}
      state={state}
      dispatch={dispatch}
      vaultId={vaultId}
      icon={icon}
      symbol={vault.token.symbol}
      userAssets={assets}
      collectionName={vault.asset.name}
      poolAddress={pool?.id as Address}
      tokenAddress={vault.id}
      liquidity={pool?.totalLiquidity ?? 1n}
      currentApy={pool?.apr?.['1m'] ?? Zero}
      currentNFTPriceInETH={currentNFTPriceInETH}
      currentTick={currentTick}
      ethMax={ethBalance?.value ?? Zero}
      standard={vault?.is1155 ? 'ERC1155' : 'ERC721'}
      userAddress={userAddress}
      amountMax={amountMax}
      withinModal={withinModal}
      settings={<Settings />}
      isNewPool={!pool?.exists}
      previousUrl={previousUrl}
    />
  );
};

export default CreateLiquidityPositionContainer;
